import { memo, useEffect, useState } from 'react'
import { Box, Grid, IconButton, Stack, TextField, Tooltip } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import Section from 'components/common/Section/Section'
import CustomText from 'components/common/CustomText/CustomText'
import CustomButton from 'components/buttons/CustomButton/CustomButton'
import CustomLabel from 'components/common/CustomLabel/CustomLabel'
import CustomDialog from 'components/dialogs/CustomDialog'
import MagicIcon from 'icons/MagicIcon'
import PencilIcon from 'icons/PencilIcon'
import SaveIcon from 'icons/SaveIcon'
import XIcon from 'icons/XIcon'
import { colors } from 'styles/colors'
import { useProductContext } from 'context/ProductContext'
import { useAnalyzeProductIngredients } from 'hooks/useAnalyzer'
import { useUpdateProductIngredients } from 'hooks/useProduct'
import { snackbarService } from 'services/snackbarService'

// The panel as it was read: the INCI name, the pack's own phrase for it, and the
// other names it goes by. Aliases and components are lists, typed one per line -
// a comma is a character that turns up inside chemical names, and a box that
// splits on it would quietly cut one in half.
interface IngredientRow {
  name: string;
  asPrinted: string;
  aliases: string;
  components: string;
}

const emptyRow = (): IngredientRow => ({
  name: '',
  asPrinted: '',
  aliases: '',
  components: '',
});

const fromProduct = (ingredients: any): IngredientRow[] => {
  if (!Array.isArray(ingredients)) return [];

  return ingredients.map((ingredient: any) => ({
    name: ingredient?.name ?? '',
    asPrinted: ingredient?.asPrinted ?? '',
    aliases: (ingredient?.aliases ?? []).join('\n'),
    components: (ingredient?.components ?? []).join('\n'),
  }));
};

const toProduct = (rows: IngredientRow[]) => {
  const lines = (text: string) =>
    text
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

  return rows
    // An ingredient with no name is a row nobody filled in.
    .filter((row) => row.name.trim() || row.asPrinted.trim())
    .map((row) => ({
      name: row.name.trim(),
      asPrinted: row.asPrinted.trim(),
      aliases: lines(row.aliases),
      components: lines(row.components),
    }));
};

function IngredientsDetails() {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const { product, refetch: refetchProduct } = useProductContext();
  const [rows, setRows] = useState<IngredientRow[]>([]);
  const [removingRow, setRemovingRow] = useState<number | null>(null);
  // A long list that is only read when something about it is in question, so it
  // stays folded away until asked for - the same as the label text above it.
  const [isVisible, setIsVisible] = useState<boolean>(false);

  useEffect(() => {
    setRows(fromProduct(product.ingredients));
  }, [product.ingredients]);

  const { refetch, isFetching } = useAnalyzeProductIngredients(product.suggestion.id, {
    enabled: false,
    onSuccess: (res: any) => {
      setRows(fromProduct(res));
      setIsEditing(true);
    },
    onError: (error: any) => {
      console.log('error', error)
    },
    select: (res: any) => res.data,
  });

  const { mutate: updateMutation, isLoading } = useUpdateProductIngredients(
    () => {
      setIsEditing(false);
      snackbarService.showSnackbar('Product ingredients has been updated successfully', 'success');
      refetchProduct();
    },
    (err: any) => {
      snackbarService.showSnackbar('Could not update the ingredients', 'error');
    },
  )

  const setField = (index: number, field: keyof IngredientRow, value: string) =>
    setRows((previous) =>
      previous.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
    );

  const handleSave = () => {
    updateMutation({ id: product.id, ingredients: toProduct(rows) as any });
  }

  const handleCancel = () => {
    setRows(fromProduct(product.ingredients));
    setIsEditing(false);
  }

  return (
    <Box mt={3}>
      <Section
        title="Product Ingredients"
        subtitle={rows.length ? `${rows.length} listed` : undefined}
        buttons={[
          ...(isEditing
            ? [
              {
                icon: <MagicIcon size={20} color={colors.primary} />,
                title: 'Analyze',
                onClick: () => refetch(),
                loading: isFetching,
                disabled: isLoading,
              },
              {
                icon: <SaveIcon size={20} color={colors.success} />,
                title: 'Save',
                onClick: () => handleSave(),
                loading: isLoading,
                disabled: isFetching,
              },
              {
                icon: <XIcon size={15} color={colors.error} />,
                title: 'Cancel',
                onClick: () => handleCancel(),
                disabled: isFetching || isLoading,
              },
            ]
            : [
              {
                icon: <PencilIcon size={20} color={colors.primary} />,
                title: 'Edit',
                onClick: () => {
                  setIsVisible(true);
                  setIsEditing(true);
                },
                disabled: isFetching || isLoading,
              }
            ]),
        ]}
        collapsed={!isVisible}
        onToggle={isEditing ? undefined : () => setIsVisible((prev) => !prev)}
        content={isVisible &&
          <Grid size={{ xs: 12 }}>
            {rows.length === 0 && !isEditing && (
              <CustomText
                text={'No ingredients read off this pack yet.'}
                fontSize={14}
                color={colors.gray}
              />
            )}

            <Stack gap={2}>
              {rows.map((row, index) => (
                <Box
                  key={index}
                  sx={{
                    border: `1px solid ${colors.lightgray || '#e0e0e0'}`,
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={1.5}
                  >
                    <CustomText text={`${index + 1}. ${row.name || row.asPrinted || 'Ingredient'}`} fontWeight={600} />

                    {isEditing && (
                      <Tooltip title="Remove this ingredient">
                        <IconButton color="error" onClick={() => setRemovingRow(index)}>
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>

                  <Grid container spacing={1.5}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <CustomLabel label={'INCI Name'} />
                      <TextField
                        size="small"
                        fullWidth
                        value={row.name}
                        disabled={!isEditing}
                        onChange={(event) => setField(index, 'name', event.target.value)}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                      <CustomLabel label={'As Printed'} />
                      <TextField
                        size="small"
                        fullWidth
                        value={row.asPrinted}
                        disabled={!isEditing}
                        onChange={(event) => setField(index, 'asPrinted', event.target.value)}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                      <CustomLabel label={'Aliases'} note={'One per line'} />
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        minRows={2}
                        value={row.aliases}
                        disabled={!isEditing}
                        onChange={(event) => setField(index, 'aliases', event.target.value)}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                      <CustomLabel label={'Components'} note={'One per line'} />
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        minRows={2}
                        value={row.components}
                        disabled={!isEditing}
                        onChange={(event) => setField(index, 'components', event.target.value)}
                      />
                    </Grid>
                  </Grid>
                </Box>
              ))}

              {isEditing && (
                <CustomButton
                  text={'Add Ingredient'}
                  variant={'outlined'}
                  startIcon={<AddIcon />}
                  height={40}
                  fullWidth
                  onClick={() => setRows((previous) => [...previous, emptyRow()])}
                />
              )}
            </Stack>
          </Grid>
        }
      />

      <CustomDialog
        open={removingRow !== null}
        title={'Remove Ingredient'}
        message={'This removes the ingredient from the panel. It is only stored once you save.'}
        buttons={[
          <CustomButton
            key={'cancel'}
            variant={'outlined'}
            text={'Cancel'}
            textColor={'error'}
            height={35}
            onClick={() => setRemovingRow(null)}
          />,
          <CustomButton
            key={'remove'}
            variant={'filled'}
            color={'error'}
            text={'Remove'}
            height={35}
            onClick={() => {
              setRows((previous) => previous.filter((_row, i) => i !== removingRow));
              setRemovingRow(null);
            }}
          />,
        ]}
      />
    </Box>
  )
}

export default memo(IngredientsDetails)
